import React, { useState, useRef, useEffect } from "react";

function CourseBanner({ data }) {
  const AWS = process.env.REACT_APP_AWS_HOST;
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const descriptionRef = useRef(null);
  const programCategoryId = sessionStorage.getItem("program_category_id")
  const instituteId = sessionStorage.getItem("instituteId")
  const CurrentProgramId = data?.course_category;

  // Calculate chapter and quiz counts
  const getChapterCount = () => {
    if (!data?.modules || !Array.isArray(data.modules)) return 0;
    return data.modules.reduce((count, module) => {
      if (module.sequences) {
        // Convert sequences object to array of values
        const sequencesArray = Object.values(module.sequences);
        return count + sequencesArray.filter(seq => seq.type === "Chapter").length;
      }
      return count;
    }, 0);
  };

  const getQuizCount = () => {
    if (!data?.modules || !Array.isArray(data.modules)) return 0;
    return data.modules.reduce((count, module) => {
      if (module.sequences) {
        // Convert sequences object to array of values
        const sequencesArray = Object.values(module.sequences);
        return count + sequencesArray.filter(seq =>
          seq.type === "Quiz" || seq.type === "Assignment"
        ).length;
      }
      return count;
    }, 0);
  };

  const chapterCount = getChapterCount();
  const quizCount = getQuizCount();

  // Get clean description text
  const getCleanDescription = () => {
    return data?.short_description?.replace(/<[^>]*>/g, '') || data?.description?.replace(/<[^>]*>/g, '') || 'Description is Loading...';
  };

  const cleanDescription = getCleanDescription();

  // Check if text is truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (descriptionRef.current && cleanDescription !== 'Description is Loading...') {
        const element = descriptionRef.current;
        // Create a clone to measure full height without line-clamp
        const clone = element.cloneNode(true);
        clone.style.position = 'absolute';
        clone.style.visibility = 'hidden';
        clone.style.height = 'auto';
        clone.style.maxHeight = 'none';
        clone.style.webkitLineClamp = 'none';
        clone.style.display = 'block';
        element.parentNode.appendChild(clone);

        const isOverflowing = clone.offsetHeight > element.offsetHeight;
        setIsTextTruncated(isOverflowing);

        element.parentNode.removeChild(clone);
      }
    };

    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(checkTruncation, 100);
    return () => clearTimeout(timer);
  }, [cleanDescription]);

  // Handle mouse enter for tooltip - only show if text is truncated
  const handleMouseEnter = (e) => {
    if (isTextTruncated) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + window.scrollX,
        y: rect.bottom + window.scrollY + 5
      });
      setShowTooltip(true);
    }
  };

  // Handle mouse leave for tooltip
  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div className="col-md-12">
      <div className="card overflow-hidden backimg1" style={{
        backgroundImage: window.innerWidth > 768 ? `url(${AWS}${data?.course_banner || data?.banner})` : 'none',
        backgroundColor: window.innerWidth <= 768 ? '#f8f9fa' : 'transparent',
        backgroundSize: 'cover'
      }}>
        <div className="card-body bg-marketplace d-flex1">
          <div className="flex-grow-1 col-md-7">
            <h1 className="banner-title">{data ? data.title : "Title Loading"}</h1>
          </div>
          <div className="row mt-2">
            <div className="col-md-6">
              <h5
                ref={descriptionRef}
                className="mb-3 fs-15"
                style={{
                  height: "auto",
                  cursor: isTextTruncated ? "help" : "default",
                  position: "relative",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical"
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onError={() => "Description is not available"}
              >
                {cleanDescription}
              </h5>

              {/* Tooltip - only show if text is truncated */}
              {showTooltip && isTextTruncated && cleanDescription !== 'Description is Loading...' && (
                <div
                  className="tooltip-custom"
                  style={{
                    position: "fixed",
                    left: `${tooltipPosition.x}px`,
                    top: `${tooltipPosition.y}px`,
                    backgroundColor: "white",
                    color: "#495057",
                    padding: "12px 16px",
                    borderRadius: "6px",
                    fontSize: "15px",
                    fontWeight: 500,
                    lineHeight: "1.2",
                    maxWidth: "400px",
                    wordWrap: "break-word",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgb(253, 253, 253)",
                    border: "1px solid #555",
                    animation: "fadeIn 0.2s ease-in-out"
                  }}
                >
                  {cleanDescription}
                </div>
              )}

              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  <h5 className="mb-0">
                    <i className="ri-time-line me-2" style={{ color: "#36348E" }}></i>
                    <span className="fw-bold fs-16">Total Hours : {data ? (data.duration || data.over_all_duration || "Loading...") : "Loading..."}</span>
                  </h5>
                </li>
                {data?.totalVideos > 0 && (
                  <li className="mb-2">
                    <h5 className="mb-0">
                      <i className="ri-book-line me-2" style={{ color: "#36348E" }}></i>
                      <span className="fw-bold fs-16">Total Video Lessons: {data?.totalVideos}</span>
                    </h5>
                  </li>
                )}
                {(data?.totalQuizzes > 0 || data?.totalAssignments > 0) && (
                  <li className="mb-2">
                    <h5 className="mb-0">
                      <i className="ri-question-line me-2" style={{ color: "#36348E" }}></i>
                      <span className="fw-bold fs-16">
                        {data?.totalQuizzes > 0 && data?.totalAssignments > 0
                          ? `${CurrentProgramId === 14 ? "Total Assessments" : "Total Quizzes"} : ${data.totalQuizzes} | Assignments : ${data.totalAssignments}`
                          : data?.totalQuizzes > 0
                            ? `${CurrentProgramId === 14 ? "Total Assessments" : "Total Quizzes"} : ${data.totalQuizzes}`
                            : `Total Assignments : ${data.totalAssignments}`}
                      </span>
                    </h5>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for fade-in animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default CourseBanner;